import React, { useEffect, useState } from "react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import { Eye, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import api from "@/lib/api";

export default function PublicTrustPage() {
  const { org } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({
    headline: "", mission_long: "",
    show_donations: true, show_campaigns: true, show_programs: true, show_audit_summary: true,
    impact_stories: [], annual_reports: [], transparency_docs: [],
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/api/trust-page").then((r) => setForm({ ...form, ...r.data })); }, []); // eslint-disable-line

  const save = async () => {
    try {
      setBusy(true);
      await api.put("/api/trust-page", form);
      toast({ title: "Trust page saved" });
    } catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail, variant: "destructive" }); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader title="Public Trust Page" subtitle="What the public sees about this organization — mission, programs, campaigns, and transparency docs.">
        {org && <a href={`/donor/${org.slug}`} target="_blank" rel="noreferrer" data-testid="trust-preview"
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm">
          <Eye size={14} /> Preview public page
        </a>}
      </PageHeader>
      <PageBody>
        <div className="max-w-3xl space-y-6">
          <section className="rounded-xl border border-border bg-card p-5">
            <h3 className="font-medium text-sm mb-3">Hero</h3>
            <div className="space-y-3 text-sm">
              <input placeholder="Headline shown above the mission" value={form.headline || ""} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="w-full px-3 py-2 rounded border border-border bg-background" data-testid="trust-headline" />
              <textarea placeholder="Long-form mission (markdown supported)" rows={5} value={form.mission_long || ""} onChange={(e) => setForm({ ...form, mission_long: e.target.value })} className="w-full px-3 py-2 rounded border border-border bg-background" />
            </div>
          </section>

          <section className="rounded-xl border border-border bg-card p-5">
            <h3 className="font-medium text-sm mb-3">What to show publicly</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                ["show_donations", "Donations totals"],
                ["show_campaigns", "Active campaigns"],
                ["show_programs", "Programs list"],
                ["show_audit_summary", "Audit summary (approval timeliness)"],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </section>

          <button onClick={save} disabled={busy} data-testid="trust-save" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground">
            {busy && <Loader2 size={14} className="animate-spin" />} Save trust page
          </button>
        </div>
      </PageBody>
    </>
  );
}
